import React, { FunctionComponent, ReactNode } from 'react'
import { MessageDescriptor, useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { formatMessageHelper, getStringFromMessage } from '@lyrahealth-inc/shared-app-logic/src/features/intl'

import { BodyText, Subhead } from '../../atoms'
import { Accordion, AccordionGroup } from '../../molecules'
import { SubheadSize } from '../../styles'
import { ThemeType } from '../../utils'
import { tID } from '../../utils/utils'

export type FaqQuestion = {
  question: MessageDescriptor | string | React.JSX.Element | ReactNode
  /** Used in body text */
  answer?: string | React.JSX.Element | ReactNode
  /** Render as is, without body text */
  answerElement?: React.JSX.Element
  hideQuestion?: boolean
}

export interface FaqAccordionProps {
  title: string | React.JSX.Element
  questions: FaqQuestion[]
  onQuestionPress?: (question: string) => void
  testId?: string
  style?: ViewStyle
  headingLevel?: string
}

const AccordionItem = styled(Accordion)<{ theme: ThemeType }>(({ theme }) => ({
  borderBottomWidth: '1px',
  borderColor: theme.colors.borderDefault,
  paddingTop: '21px',
  paddingBottom: '21px',
}))

export const FaqAccordion: FunctionComponent<FaqAccordionProps> = ({
  title,
  questions,
  onQuestionPress,
  testId,
  style,
  headingLevel,
}) => {
  const intl = useIntl()
  const { colors, breakpoints } = useTheme()

  return (
    <View style={style}>
      <Subhead
        size={breakpoints.isMobileSized ? SubheadSize.MEDIUM : SubheadSize.LARGE}
        text={title}
        level={headingLevel}
      />
      <View testID={tID(testId || 'FaqAccordion')}>
        <AccordionGroup
          accordions={questions
            .filter((faq) => {
              return !('hideQuestion' in faq && faq.hideQuestion)
            })
            .map((faq, index) => (
              <AccordionItem
                key={index}
                title={formatMessageHelper(faq.question, true, intl.formatMessage)}
                content={
                  faq.answer ? (
                    <BodyText color={colors.textSecondary} text={faq.answer} />
                  ) : faq.answerElement ? (
                    faq.answerElement
                  ) : (
                    ''
                  )
                }
                backgroundColor={colors.backgroundTransparent}
                onAccordionPress={() => {
                  onQuestionPress && onQuestionPress(getStringFromMessage(faq.question) || '')
                }}
                chevronSize={breakpoints.isMobileSized ? 24 : 32}
                showGradient={false}
              />
            ))}
          allowMultiExpand={!breakpoints.isMobileSized}
        />
      </View>
    </View>
  )
}
